import type { UserId } from '@csp/csp-common-model';
import { ElaasConsequenceCode, ElaasFormulaCodeOrIdentifier } from '@csp/csp-common-rpm-model';
import {
  AssessmentAggregateCriteriaFieldV1,
  AssessmentAggregateGroupFieldV1,
  AssessmentAggregateIncludeFieldV1,
  AssessmentAggregateQueryOperatorV1,
  AssessmentAggregateQueryTypeV1,
  AssessmentAggregateV1,
  type AssessmentAggregateQueryCriterionV1,
} from '@csp/dmdp-api-assessment-dto';
import { AssessmentAlgorithmResult } from '../model/AssessmentAlgorithmResult';
import { AssessmentCustomStatusType } from '../model/AssessmentCustomStatusType';
import { AssessmentCustomStatusValue } from '../model/AssessmentCustomStatusValue';
import { AssessmentOutcomeKey } from '../model/AssessmentOutcomeKey';

// const sortQuery = (): AssessmentAggregateQuerySortV1[] => [
//   {
//     field: AssessmentAggregateSortFieldV1.CUSTOM_STATUS_VALUE,
//     subField: AssessmentAggregateSortFieldV1.CUSTOM_STATUS_VALUE,
//     order: AssessmentAggregateQuerySortOrderV1.ASC,
//   },
//   {
//     field: AssessmentAggregateSortFieldV1.CREATED_AT,
//     subField: AssessmentAggregateSortFieldV1.CREATED_AT,
//     order: AssessmentAggregateQuerySortOrderV1.DESC,
//   },
// ];

const formulaCodesQuery = (
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
): AssessmentAggregateQueryCriterionV1[] => [
  {
    fieldCriterion: {
      field: AssessmentAggregateCriteriaFieldV1.ALGORITHM_CODE,
      type: AssessmentAggregateQueryTypeV1.IN,
      values: [...formulaCodes],
    },
  },
  {
    operator: AssessmentAggregateQueryOperatorV1.AND,
    criteria: [
      {
        fieldCriterion: {
          field: AssessmentAggregateCriteriaFieldV1.CUSTOM_STATUS_TYPE,
          type: AssessmentAggregateQueryTypeV1.EQ,
          values: [AssessmentCustomStatusType.VIEW_STATUS],
        },
      },
      {
        fieldCriterion: {
          field: AssessmentAggregateCriteriaFieldV1.CUSTOM_STATUS_VALUE,
          type: AssessmentAggregateQueryTypeV1.IN,
          values: [AssessmentCustomStatusValue.NOT_VIEWED, AssessmentCustomStatusValue.VIEWED],
        },
      },
    ],
  },
];

const algorithmResultNotOkOrConsequencesQuery = (
  consequences: readonly ElaasConsequenceCode[],
): AssessmentAggregateQueryCriterionV1[] => [
  {
    operator: AssessmentAggregateQueryOperatorV1.OR,
    criteria: [
      {
        operator: AssessmentAggregateQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: AssessmentAggregateCriteriaFieldV1.OUTCOME_KEY,
              type: AssessmentAggregateQueryTypeV1.LIKE,
              values: [AssessmentOutcomeKey.algorithmResult],
            },
          },
          {
            fieldCriterion: {
              field: AssessmentAggregateCriteriaFieldV1.OUTCOME_VALUE,
              type: AssessmentAggregateQueryTypeV1.EQ,
              values: [AssessmentAlgorithmResult.ALGORITHM_RESULT_NOT_OK],
            },
          },
        ],
      },
      {
        operator: AssessmentAggregateQueryOperatorV1.AND,
        criteria: [
          {
            fieldCriterion: {
              field: AssessmentAggregateCriteriaFieldV1.OUTCOME_KEY,
              type: AssessmentAggregateQueryTypeV1.LIKE,
              values: [AssessmentOutcomeKey.consequence],
            },
          },
          {
            fieldCriterion: {
              field: AssessmentAggregateCriteriaFieldV1.OUTCOME_VALUE,
              type: AssessmentAggregateQueryTypeV1.IN,
              values: [...consequences],
            },
          },
        ],
      },
    ],
  },
];

const byFormulaCodesAndConsequencesQuery = (
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequences: readonly ElaasConsequenceCode[],
): AssessmentAggregateV1 => ({
  aggregateFields: [
    AssessmentAggregateIncludeFieldV1.COUNT,
    AssessmentAggregateIncludeFieldV1.MAX_ASSESSMENT_TIMESTAMP,
    AssessmentAggregateIncludeFieldV1.MAX_EVENT_ID,
  ],
  group: {
    fields: [
      AssessmentAggregateGroupFieldV1.USER_ID,
      AssessmentAggregateGroupFieldV1.ASSESSMENT_ALGORITHM_CODE,
      AssessmentAggregateGroupFieldV1.CUSTOM_STATUS_VALUE,
      AssessmentAggregateGroupFieldV1.CUSTOM_STATUS_TYPE,
      AssessmentAggregateGroupFieldV1.OUTCOME_KEY,
      AssessmentAggregateGroupFieldV1.OUTCOME_VALUE,
    ],
  },
  match: {
    criterion: {
      operator: AssessmentAggregateQueryOperatorV1.AND,
      criteria: [...formulaCodesQuery(formulaCodes), ...algorithmResultNotOkOrConsequencesQuery(consequences)],
    },
    //sort: [...sortQuery()], // TODO[!DeviceSquad!] waiting for Boost to fix this.
  },
});

const byUserIdAndFormulaCodesAndConsequencesQuery = (
  userId: UserId,
  formulaCodes: readonly ElaasFormulaCodeOrIdentifier[],
  consequences: readonly ElaasConsequenceCode[],
): AssessmentAggregateV1 => ({
  aggregateFields: [
    AssessmentAggregateIncludeFieldV1.COUNT,
    AssessmentAggregateIncludeFieldV1.MAX_ASSESSMENT_TIMESTAMP,
    AssessmentAggregateIncludeFieldV1.MAX_EVENT_ID,
  ],
  group: {
    fields: [
      AssessmentAggregateGroupFieldV1.USER_ID,
      AssessmentAggregateGroupFieldV1.ASSESSMENT_ALGORITHM_CODE,
      AssessmentAggregateGroupFieldV1.CUSTOM_STATUS_VALUE,
      AssessmentAggregateGroupFieldV1.CUSTOM_STATUS_TYPE,
      AssessmentAggregateGroupFieldV1.OUTCOME_KEY,
      AssessmentAggregateGroupFieldV1.OUTCOME_VALUE,
    ],
  },
  match: {
    criterion: {
      operator: AssessmentAggregateQueryOperatorV1.AND,
      criteria: [
        {
          fieldCriterion: {
            field: AssessmentAggregateCriteriaFieldV1.USER_ID,
            type: AssessmentAggregateQueryTypeV1.EQ,
            values: [userId],
          },
        },
        ...formulaCodesQuery(formulaCodes),
        ...algorithmResultNotOkOrConsequencesQuery(consequences),
      ],
    },
    //sort: [...sortQuery()], // TODO[!DeviceSquad!] waiting for Boost to fix this.
  },
});

export const RpmAssessmentAggregateQuery = {
  byFormulaCodesAndConsequencesQuery,
  byUserIdAndFormulaCodesAndConsequencesQuery,
};
